/**
 * Compras: where cost enters the system.
 *
 * This reducer is the only place weighted-average cost is computed, and every margin number the
 * business will ever see traces back to it. Three things it has to get exactly right:
 *
 *   1. Freight must allocate to the last centavo, or the journal entry will not balance.
 *   2. A USD invoice converts at the rate on the purchase date, not today's.
 *   3. Two lines of the same product in one purchase must average against each other, which
 *      means tracking pending state — the reader cannot see effects that have not been applied.
 */

import {
  ZERO,
  addMoney,
  addQty,
  centavos,
  fechaEnNicaragua,
  formatMoney,
  fxRate,
  repartirProporcional,
  saleToBase,
  subMoney,
  subQty,
  unitCostFromTotal,
  usdToCordobas,
  weightedAverageCost,
  costOf,
  orderKey,
  ZERO_COST,
  ZERO_QTY,
  esCuentaEnDolares,
  type AnyDomainEvent,
  type Centavos,
  type CuentaBanco,
  type Milli,
  type Settings,
  type UnitCost,
} from '@abastece/shared';
import { ajusteVendidoAntes, cuentaInventario, momentoDe, type Recibido } from '../inventario.js';
import { credit, debit, entry, type JournalLine } from '../ledger/journal.js';
import type { AccountKey } from '../ledger/accounts.js';
import { cuentaCerradaParaSiempre, cuentaDeTransferencia, lineaDeBanco } from './bancos.js';
import { devolucionDePago, efectivoEsperado, pagoDeCaja } from './caja.js';
import { contadaDespues, lugarEnElLog, yaLaContaron } from './contadas.js';
import { conteoAlcanzado, corregidos, reencontrar, type Reencontrado } from './encontrado.js';
import { noCuadraLaCorreccion, type ConteoQueCambia } from './cuadran.js';
import {
  esDeudaAnterior,
  existenciaId,
  loteId,
  type ApplyResult,
  type Compra,
  type CompraLinea,
  type Effect,
  type Existencia,
  type FormaPago,
  type Lote,
  type Reader,
  type SesionCaja,
  type Venta,
  type VentaLinea,
} from '../state.js';

/**
 * Where a supplier's payment comes from, unless it came out of an open till.
 *
 * Paid out of the drawer, a purchase says which till (`sesionId`), comes off «Caja (gaveta)», and
 * that corte stops expecting the money (`pagoDeCaja`). Any other cash is general cash.
 */
const CUENTA_PAGO: Record<FormaPago, AccountKey> = {
  efectivo_cordobas: 'CAJA_FUERTE',
  // Dollars too: the corte takes every dollar out of the drawer, so general cash is where they are.
  efectivo_dolares: 'CAJA_FUERTE',
  transferencia: 'BANCOS',
  billetera: 'BILLETERA_MOVIL',
  credito: 'CUENTAS_POR_PAGAR',
};

/**
 * A line on the account a payment came from or goes back to: by transfer, the bank account it named
 * (`bancos.ts`), a dollar account's carrying the dollars it moved.
 */
function lineaDelPago(
  lado: 'debe' | 'haber',
  forma: FormaPago,
  cuenta: string | null | undefined,
  monto: Centavos,
  dolares: number,
  memo?: string,
): JournalLine {
  if (forma === 'transferencia') return lineaDeBanco(lado, cuentaDeTransferencia(cuenta as CuentaBanco | null | undefined), monto, dolares, memo);
  return lado === 'debe' ? debit(CUENTA_PAGO[forma], monto, memo) : credit(CUENTA_PAGO[forma], monto, memo);
}

interface EstadoStock {
  cantidad: Milli;
  costoPromedio: UnitCost;
}

export async function reduceCompra(
  event: AnyDomainEvent,
  read: Reader,
): Promise<ApplyResult | null> {
  const rev = orderKey(event);

  switch (event.type) {
    case 'compra.registrada':
      return registrar(event, read, rev);
    case 'compra.anulada':
      return anular(event, read, rev);
    case 'compra.costo_corregido':
      return corregirCosto(event, read, rev);
    case 'compra.cantidad_corregida':
      return corregirCantidad(event, read, rev);
    case 'compra.forma_pago_corregida':
      return corregirPago(event, read, rev);
    case 'inventario.saldo_inicial':
      return saldoInicial(event, read, rev);
    case 'compra.saldo_inicial':
      return deudaAnterior(event, read, rev);
    default:
      return null;
  }
}

async function registrar(
  event: Extract<AnyDomainEvent, { type: 'compra.registrada' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;

  const proveedor = await read.proveedor(p.proveedorId);
  if (!proveedor) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'El proveedor no existe' }] };
  }

  const yaExiste = await read.compra(p.compraId);
  if (yaExiste) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa compra ya fue registrada' }] };
  }
  const config = await read.config();
  for (const cuenta of [p.cuenta, ...(p.pagos ?? []).map((x) => x.cuenta)]) {
    const cerrada = cuentaCerradaParaSiempre(config, cuenta);
    if (cerrada) return { effects: [], rejections: [{ eventId: event.id, reason: cerrada }] };
  }

  // Freight rides on line value. Largest-remainder, so the parts sum to exactly the freight paid.
  const pesos = p.lineas.map((l) => centavos(l.costoTotal));
  const fletes = repartirProporcional(centavos(p.flete), pesos);
  /*
   * The invoice's IVA rides the same way (the owner, 2026-10-11): the shop charges none, so what it
   * pays is part of what the goods cost, every line by its value, the exempt ones too.
   */
  const ivas = repartirProporcional(centavos(p.iva), pesos);

  /** Paid in parts (`pagos`): each its own way, and `formaPago` what they leave. */
  const partes = p.pagos ?? [];

  /**
   * Paid in dollars out of general cash, the dollars leave it at the official rate — what they
   * are carried at there — and a part in dollars takes the whole purchase with it: one rate to a
   * purchase. Any other way, at the rate the purchase says: what the córdobas paid, or owed, came to.
   */
  /** Paid from a dollar account (`bancos.ts`): in its dollars, at the official rate, as dollars from the safe are. */
  const deCuentaEnDolares = p.formaPago === 'transferencia' && p.cuenta !== undefined && esCuentaEnDolares(p.cuenta);
  const conDolares =
    p.formaPago === 'efectivo_dolares' || deCuentaEnDolares || partes.some((x) => x.formaPago === 'efectivo_dolares');
  const tasa = p.moneda !== 'USD' ? null : fxRate(conDolares ? config.fxTasaOficial : p.tasaCambio!);

  const effects: Effect[] = [];
  const lineas: CompraLinea[] = [];
  /** Pending stock per product, because the reader cannot see effects not yet applied. */
  const pendiente = new Map<string, EstadoStock>();
  /** Batches as this purchase leaves them: two lines of one batch both go into it. */
  const lotes = new Map<string, Lote>();
  let totalCordobas: Centavos = ZERO;

  for (let i = 0; i < p.lineas.length; i++) {
    const l = p.lineas[i]!;

    const producto = await read.producto(l.productoId);
    if (!producto) {
      return {
        effects: [],
        rejections: [{ eventId: event.id, reason: `Producto desconocido en la línea ${i + 1}` }],
      };
    }

    const cantidad = l.cantidadBase as Milli;
    const enMoneda = addMoney(addMoney(centavos(l.costoTotal), fletes[i]!), ivas[i]!);
    const enCordobas = tasa ? usdToCordobas(enMoneda, tasa) : enMoneda;

    const clave = existenciaId(l.productoId, p.ubicacion);
    let estado = pendiente.get(clave);
    if (!estado) {
      const actual = await read.existencia(l.productoId, p.ubicacion);
      estado = {
        cantidad: actual?.cantidad ?? ZERO_QTY,
        costoPromedio: actual?.costoPromedio ?? ZERO_COST,
      };
    }

    const antes = { ...estado };
    const costoEntrante = unitCostFromTotal(enCordobas, cantidad);
    const nuevoCosto = weightedAverageCost(
      estado.cantidad,
      estado.costoPromedio,
      cantidad,
      costoEntrante,
    );
    const nuevaCantidad = addQty(estado.cantidad, cantidad);

    pendiente.set(clave, { cantidad: nuevaCantidad, costoPromedio: nuevoCosto });

    lineas.push({
      productoId: l.productoId,
      cantidadBase: cantidad,
      costoTotal: centavos(l.costoTotal),
      fleteAsignado: fletes[i]!,
      ...(p.iva > 0 ? { ivaAsignado: ivas[i]! } : {}),
      costoCordobas: enCordobas,
      // Only the FIRST line touching a product snapshots pre-purchase state; an annulment
      // restores from that one and the later lines of the same product are already included.
      antes,
      ...(l.lote ? { lote: { numero: l.lote.numero, vencimiento: l.lote.vencimiento } } : {}),
    });

    if (l.lote) {
      const id = loteId(l.productoId, l.lote.numero, p.ubicacion);
      const existente = lotes.get(id) ?? (await read.lotesDe(l.productoId, p.ubicacion)).find((x) => x.id === id);
      lotes.set(id, {
        id,
        productoId: l.productoId,
        numero: l.lote.numero,
        vencimiento: l.lote.vencimiento,
        cantidadRecibida: addQty(existente?.cantidadRecibida ?? ZERO_QTY, cantidad),
        cantidadDisponible: addQty(existente?.cantidadDisponible ?? ZERO_QTY, cantidad),
        compraId: p.compraId,
        ubicacion: p.ubicacion,
        rev,
      });
    }

    totalCordobas = addMoney(totalCordobas, enCordobas);
  }

  for (const [clave, estado] of pendiente) {
    const [productoId, ubicacion] = partirClave(clave);
    effects.push({
      kind: 'existencia.set',
      id: clave,
      row: {
        id: clave,
        productoId,
        ubicacion,
        cantidad: estado.cantidad,
        costoPromedio: estado.costoPromedio,
        ultimoMovimientoId: p.compraId,
        rev,
      },
    });
  }
  for (const [id, row] of lotes) effects.push({ kind: 'lote.upsert', id, row });

  /**
   * The parts in córdobas, each converted on its own, and what they leave for `formaPago`: the
   * total less the parts, so whatever the conversions rounded lands there and the entry balances.
   */
  const partesCordobas = partes.map((x) => (tasa ? usdToCordobas(centavos(x.monto), tasa) : centavos(x.monto)));
  const resto = partesCordobas.reduce((a, x) => subMoney(a, x), totalCordobas);
  if (partes.length > 0 && resto <= 0) {
    return {
      effects: [],
      rejections: [{ eventId: event.id, reason: 'Las partes pagan toda la compra: la última va en la forma de pago' }],
    };
  }

  /**
   * What leaves the open till: the rest when it is paid from there, and every part that is. One
   * till and one patch of it, since the reader cannot see a patch before the next one.
   */
  const gaveta = p.sesionId ?? partes.find((x) => x.sesionId)?.sesionId;
  if (partes.some((x) => x.sesionId && x.sesionId !== gaveta)) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Lo que sale de la gaveta sale de una sola caja' }] };
  }
  const deLaGaveta = partes.reduce((a, x, i) => (x.sesionId ? addMoney(a, partesCordobas[i]!) : a), p.sesionId ? resto : ZERO);
  /** What the rest is in the purchase's dollars: what leaves a dollar account that pays it. */
  const restoDolares = centavos(p.lineas.reduce((a, l) => a + l.costoTotal, p.flete + p.iva) - partes.reduce((a, x) => a + x.monto, 0));
  const deCaja = gaveta
    ? await pagoDeCaja(read, gaveta, p.sesionId ? p.formaPago : 'efectivo_cordobas', deLaGaveta, rev)
    : null;
  if (deCaja && 'rechazo' in deCaja) {
    return { effects: [], rejections: [{ eventId: event.id, reason: deCaja.rechazo }] };
  }
  if (deCaja) effects.push(deCaja.efecto);

  const compra: Compra = {
    id: p.compraId,
    proveedorId: p.proveedorId,
    fecha: p.fecha,
    documento: p.documento ?? null,
    moneda: p.moneda,
    tasaCambio: tasa,
    flete: centavos(p.flete),
    ...(p.iva > 0 ? { iva: centavos(p.iva) } : {}),
    ubicacion: p.ubicacion,
    formaPago: p.formaPago,
    vencePago: p.vencePago ?? null,
    // The till the rest came out of; each part keeps its own.
    sesionId: deCaja && p.sesionId ? p.sesionId : null,
    ...(p.cuenta ? { cuenta: p.cuenta } : {}),
    ...(deCuentaEnDolares ? { dolares: restoDolares } : {}),
    lineas,
    totalCordobas,
    // A credit purchase starts owing what the parts left; anything else was settled on the spot.
    pagado: p.formaPago === 'credito' ? subMoney(totalCordobas, resto) : totalCordobas,
    ...(partes.length > 0
      ? {
          pagos: partes.map((x, i) => ({
            id: x.pagoId,
            fecha: p.fecha,
            monto: partesCordobas[i]!,
            formaPago: x.formaPago,
            sesionId: x.sesionId ?? null,
            ...(x.cuenta ? { cuenta: x.cuenta } : {}),
            referencia: null,
            anulado: false,
            motivoAnulacion: null,
            lugar: lugarEnElLog(event),
            rev,
            conLaCompra: true as const,
          })),
        }
      : {}),
    anulada: false,
    motivoAnulacion: null,
    lugar: lugarEnElLog(event),
    momento: momentoDe(event),
    rev,
  };
  effects.push({ kind: 'compra.upsert', id: p.compraId, row: compra });

  /**
   * A USD purchase debits inventory and credits the payment account at the same rate, so no FX
   * difference arises here. The gap between the store rate and the official rate shows up on
   * the SALES side, where dollars are taken at the counter.
   */
  const pagadoCon = (forma: FormaPago, deLaGaveta: boolean, monto: Centavos, cuenta?: string, dolares = 0) =>
    deLaGaveta ? credit('CAJA_CORDOBAS', monto, 'Pagado de la gaveta') : lineaDelPago('haber', forma, cuenta, monto, dolares);
  effects.push({
    kind: 'asiento',
    entry: entry(event.id, p.fecha, `Compra a ${proveedor.nombre}${p.documento ? ` (${p.documento})` : ''}`, [
      debit(cuentaInventario(p.ubicacion), totalCordobas, `${lineas.length} línea(s)`),
      ...partes.map((x, i) => pagadoCon(x.formaPago, Boolean(x.sesionId), partesCordobas[i]!, x.cuenta)),
      pagadoCon(p.formaPago, Boolean(deCaja && p.sesionId), resto, p.cuenta, deCuentaEnDolares ? restoDolares : 0),
    ]),
  });
  const ajuste = ajusteVendidoAntes(
    event.id,
    p.fecha,
    recibidosDeCompra(lineas, p.ubicacion, (productoId) => pendiente.get(existenciaId(productoId, p.ubicacion))!),
  );
  if (ajuste) effects.push(ajuste);

  return { effects, rejections: [] };
}

/**
 * What a purchase put on each product's line: the line as it stood before the first of its purchase
 * lines, what it cost in córdobas, and the line after (`ajusteVendidoAntes`).
 */
function recibidosDeCompra(
  lineas: readonly CompraLinea[],
  ubicacion: string,
  despues: (productoId: string) => { cantidad: Milli; costoPromedio: UnitCost },
): Recibido[] {
  const porProducto = new Map<string, Recibido>();
  for (const l of lineas) {
    const r = porProducto.get(l.productoId);
    if (r) r.entrante = addMoney(r.entrante, l.costoCordobas);
    else {
      porProducto.set(l.productoId, {
        antes: l.antes,
        despues: despues(l.productoId),
        entrante: l.costoCordobas,
        cuenta: cuentaInventario(ubicacion),
      });
    }
  }
  return [...porProducto.values()];
}

/**
 * A purchase line's cost corrected after its goods have moved (`compra.costo_corregido`).
 *
 * The difference is split the way the goods went. Under average cost every exit takes its share
 * of what the purchase added, so the share still on the shelf is what stands at the purchase's
 * location now over what stood there right after it — exact when only sales and other exits
 * followed; a later purchase of the same product leans it toward the shelf, and it never takes
 * the stock below nothing. That share changes what the stock is worth, taken as exactly what its
 * value changes by, rounding and all, so the stock and the books keep agreeing to the centavo;
 * the rest, already gone, goes to cost of sales.
 *
 * The other side is where the money was: what is owed on credit; a drawer still open, whose corte
 * then expects the right amount; a drawer already counted, where the difference surfaced as a
 * shortfall or an overage, which this explains; or the account the purchase was paid from.
 *
 * The stock's last movement stays the purchase's: a correction moves no goods, and a purchase
 * nothing else has touched can still be voided, the correction with it.
 */
/**
 * Where a cost correction's difference goes, as the money it was paid with stands now: what is
 * owed, on credit; a drawer nobody has counted since the purchase (`gaveta`); one counted since, at
 * a handover or its corte (`corte`); the account it was paid from (`cuenta`), unless a count of it
 * came after (`conteo`: general cash at a count of the safe, the bank or the wallet at a statement,
 * `contadas.ts`). A count after the purchase already found what was really paid and booked the
 * difference, so what that count found moves instead of the money a second time (2026-10-13;
 * `reencontrarElPago`, each over/short account once, 2026-10-15). Pure, so the screen says what
 * the books will do.
 */
export function adondeVaLaCorreccion(
  compra: Pick<Compra, 'formaPago' | 'cuenta' | 'lugar' | 'fecha'>,
  sesion: Pick<SesionCaja, 'estado' | 'entregadaRev'> | undefined,
  config: Settings,
): { destino: 'deuda' | 'gaveta' | 'corte' | 'cuenta' | 'conteo'; cuenta: AccountKey } {
  const lugar = compra.lugar ?? -Infinity;
  if (compra.formaPago === 'credito') return { destino: 'deuda', cuenta: 'CUENTAS_POR_PAGAR' };
  if (sesion) {
    const contada = sesion.estado !== 'abierta' || (sesion.entregadaRev ?? -Infinity) > lugar;
    return { destino: contada ? 'corte' : 'gaveta', cuenta: 'CAJA_CORDOBAS' };
  }
  const cuenta = compra.formaPago === 'transferencia' ? cuentaDeTransferencia(compra.cuenta as CuentaBanco | null | undefined) : CUENTA_PAGO[compra.formaPago];
  return { destino: contadaDespues(config, cuenta, lugar, compra.fecha) ? 'conteo' : 'cuenta', cuenta };
}

async function corregirCosto(
  event: Extract<AnyDomainEvent, { type: 'compra.costo_corregido' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });
  const compra = await read.compra(p.compraId);
  if (!compra) return rechazo('Esa compra no existe');
  if (compra.anulada) return rechazo('Esa compra está anulada');
  const linea = compra.lineas[p.indice];
  if (!linea) return rechazo('Esa línea no existe');
  // What went back was credited at the old cost; a return is how that gets settled with the supplier.
  if ((linea.devuelta ?? 0) > 0) return rechazo('Esa línea ya tiene devoluciones al proveedor');

  const tasa = compra.moneda === 'USD' && compra.tasaCambio ? fxRate(compra.tasaCambio) : null;
  // The line's freight and IVA stay what they were: the invoice's, spread when it came in.
  const enMoneda = addMoney(addMoney(centavos(p.costoTotal), linea.fleteAsignado), linea.ivaAsignado ?? ZERO);
  const nuevoCordobas = tasa ? usdToCordobas(enMoneda, tasa) : enMoneda;
  const diferencia = subMoney(nuevoCordobas, linea.costoCordobas);
  if (diferencia === 0) return rechazo('Ese ya es el costo de la línea');
  const totalCordobas = addMoney(compra.totalCordobas, diferencia);
  if (compra.formaPago === 'credito' && totalCordobas < compra.pagado + (compra.devueltoACuenta ?? 0)) {
    return rechazo('Al proveedor ya se le abonó más de lo que costaría esa compra');
  }
  // Paid in parts, the parts are money that went: the rest takes the correction, never below nothing.
  const enPartes = (compra.pagos ?? []).filter((x) => x.conLaCompra && !x.anulado).reduce((a, x) => addMoney(a, x.monto), ZERO);
  if (totalCordobas < enPartes) return rechazo('Lo pagado en partes ya pasa de lo que costaría esa compra');

  /*
   * What each unit cost right after the purchase, as it was entered and as corrected; and the sales
   * that took that cost, costed again at the corrected one, as a corrected quantity costs them
   * (2026-10-20). Only booked on «Costo de ventas», their own rows kept the old cost: the profit by
   * product came out off the books by it, a void or a return of one put its goods back at the old
   * cost, and a quantity corrected after found none of them and brought what was missing in at nothing.
   */
  const comoEsta = (i: number) => compra.lineas[i]!.cantidadBase;
  const costoAntes = costoQueDejo(compra, linea.productoId, comoEsta);
  const corregida = { ...compra, lineas: compra.lineas.map((l, i) => (i === p.indice ? { ...l, costoCordobas: nuevoCordobas } : l)) };
  const costoDespues = costoQueDejo(corregida, linea.productoId, comoEsta);
  const recosteo = recostearVentas(
    compra,
    linea.productoId,
    costoAntes,
    costoDespues,
    await read.ventasDeProducto(linea.productoId, compra.ubicacion, compra.fecha),
  );
  const actual = await read.existencia(linea.productoId, compra.ubicacion);
  const effects: Effect[] = recosteo.ventas.map(
    (v): Effect => ({ kind: 'venta.patch', id: v.venta.id, rev, patch: { lineas: v.lineas, costoTotal: v.costoTotal } }),
  );
  let aEstante: Centavos = ZERO;
  if (actual) {
    const valor = costOf(actual.costoPromedio, actual.cantidad);
    let costoPromedio = actual.costoPromedio;
    if (actual.costoPromedio === costoAntes) {
      // Still at the purchase's cost: it takes the corrected one exactly, as if typed right.
      costoPromedio = costoDespues;
    } else if (actual.cantidad > 0) {
      // Mixed since: what its sales did not take, into what is left, never below nothing.
      const parte = Math.max(diferencia - recosteo.aVendido, -valor);
      costoPromedio = unitCostFromTotal(centavos(valor + parte), actual.cantidad);
    }
    aEstante = subMoney(costOf(costoPromedio, actual.cantidad), valor);
    if (costoPromedio !== actual.costoPromedio) {
      /*
       * A movement of this stock, unless the purchase corrected is the one that last moved it: its
       * void still undoes it whole, putting back what stood before it. Left as it was, the void of a
       * later purchase put back what stood before that one, as if this correction had never been, and
       * the stock no longer tied to the books (2026-10-20, a random walk: C$ 441.00 apart).
       */
      const ultimoMovimientoId = actual.ultimoMovimientoId === compra.id ? compra.id : event.id;
      effects.push({ kind: 'existencia.set', id: actual.id, row: { ...actual, costoPromedio, ultimoMovimientoId, rev } });
    }
  }
  const aVendido = subMoney(diferencia, aEstante);

  const sesion = compra.sesionId ? await read.sesionCaja(compra.sesionId) : undefined;
  const mas = diferencia > 0;
  const config = await read.config();
  const donde = adondeVaLaCorreccion(compra, sesion, config);
  const contadaLaCaja = donde.destino === 'corte';
  // Counted since, the money already was what the count found: what it found moves instead.
  const encontrado = await reencontrarElPago(read, config, donde, compra, -diferencia, lugarEnElLog(event));
  if (encontrado && 'rechazo' in encontrado) return rechazo(encontrado.rechazo);
  let contrapartida: AccountKey;
  if (donde.destino === 'deuda') contrapartida = 'CUENTAS_POR_PAGAR';
  else if (donde.destino === 'gaveta' && sesion) {
    contrapartida = 'CAJA_CORDOBAS';
    effects.push({ kind: 'sesion.patch', id: sesion.id, rev, patch: { pagado: addMoney(sesion.pagado ?? ZERO, diferencia) } });
  } else if (donde.destino === 'corte') contrapartida = mas ? 'FALTANTE_CAJA' : 'SOBRANTE_CAJA';
  else if (donde.destino === 'conteo') {
    contrapartida = donde.cuenta === 'CAJA_FUERTE' ? (mas ? 'FALTANTE_GENERAL' : 'SOBRANTE_GENERAL') : 'DIFERENCIAS_CONCILIACION';
  } else contrapartida = donde.cuenta;
  /** Paid from a dollar account: the line's dollars moved with its cost, and what the account paid with them. */
  const difDolares = compra.dolares != null ? centavos(p.costoTotal - linea.costoTotal) : 0;

  effects.push({
    kind: 'compra.patch',
    id: compra.id,
    rev,
    patch: {
      lineas: compra.lineas.map((l, i) =>
        i === p.indice ? { ...l, costoTotal: centavos(p.costoTotal), costoCordobas: nuevoCordobas } : l,
      ),
      totalCordobas,
      ...(compra.formaPago === 'credito' ? {} : { pagado: addMoney(compra.pagado, diferencia) }),
      ...(compra.dolares != null ? { dolares: centavos(compra.dolares + difDolares) } : {}),
    },
  });

  const lado = (cuenta: AccountKey, monto: Centavos, texto?: string) =>
    cuenta === contrapartida && compra.dolares != null
      ? lineaDeBanco(monto > 0 ? 'debe' : 'haber', cuenta as CuentaBanco, centavos(Math.abs(monto)), Math.abs(difDolares), texto)
      : monto > 0
        ? debit(cuenta, monto, texto)
        : credit(cuenta, centavos(-monto), texto);
  effects.push({
    kind: 'asiento',
    entry: entry(event.id, fechaEnNicaragua(event.clientTs), `Costo de compra corregido: ${p.motivo}`, [
      ...(aEstante !== 0 ? [lado(cuentaInventario(compra.ubicacion), aEstante, 'Lo que queda')] : []),
      ...(aVendido !== 0 ? [lado('COSTO_VENTAS', aVendido, 'Lo ya vendido')] : []),
      ...(encontrado ? encontrado.lineas : [lado(contrapartida, centavos(-diferencia), contadaLaCaja ? 'Contado en el corte' : undefined)]),
    ]),
  });
  const cambian = corregidos(encontrado);
  if (Object.keys(cambian).length > 0) {
    effects.push({ kind: 'config.set', clave: 'conteosCorregidos', valor: { ...config.conteosCorregidos, ...cambian }, rev });
  }
  const razon = noCuadraLaCorreccion(effects, {
    sesiones: donde.destino === 'gaveta' && sesion ? [sesion] : [],
    relevadas: new Set(),
    conteos: conteosQueCambian(encontrado),
    corregidos: config.conteosCorregidos,
  });
  if (razon) return rechazo(`La corrección no cuadra (${razon}): no se asentó`);
  return { effects, rejections: [] };
}

/** The counts a correction reached, each once with what it found first (`noCuadraLaCorreccion`). */
function conteosQueCambian(...reencontrados: (Reencontrado | null)[]): ConteoQueCambia[] {
  const out: ConteoQueCambia[] = [];
  for (const r of reencontrados) {
    if (r && !r.inicial && !out.some((c) => c.id === r.id)) out.push({ id: r.id, cuentas: r.cuentas, antes: r.antes });
  }
  return out;
}

/**
 * Money a purchase's correction moves on where it was paid from, once that was counted since: a
 * till's drawer, at a handover or its corte, or general cash, at a count of the safe. The count
 * found it as it was, so what it found moves the other way (`encontrado.ts`). `valor`, what that
 * money should have held more (negative: less). Null when nothing counted it since, or the money
 * is a bank's, whose statement keeps its differences on one account. The sheets say it before
 * saving from the same reading, with `hasta` past everything there is.
 */
export async function reencontrarElPago(
  read: Reader,
  config: Settings,
  donde: { destino: string; cuenta: AccountKey },
  pago: { sesionId?: string | null; lugar?: number | null },
  valor: number,
  hasta: number,
): Promise<Reencontrado | { rechazo: string } | null> {
  const desde = pago.lugar ?? -Infinity;
  const conteo =
    donde.destino === 'corte' && pago.sesionId
      ? await conteoAlcanzado(read, config, { sesionId: pago.sesionId }, desde, hasta)
      : donde.destino === 'conteo' && donde.cuenta === 'CAJA_FUERTE'
        ? await conteoAlcanzado(read, config, { general: true }, desde, hasta)
        : null;
  if (!conteo) {
    // A drawer counted since that no count can be found for: the books would not tie, so not at all.
    return donde.destino === 'corte' ? { rechazo: 'No se encuentra el conteo de esa caja después de la compra' } : null;
  }
  const cual = donde.destino === 'corte' ? 'el conteo de esa caja' : 'el conteo del efectivo general';
  return reencontrar(conteo, valor, cual, donde.destino === 'corte' ? 'Lo encontró el conteo de la caja' : 'Lo encontró el conteo del efectivo general');
}

/**
 * What each unit of a product cost right after a purchase: its lines of that product averaged into
 * the stock as it stood before the first of them, as `registrar` did, each line at the quantity
 * `cantidadDe` gives it.
 */
function costoQueDejo(compra: Compra, productoId: string, cantidadDe: (indice: number) => Milli): UnitCost {
  let estado: EstadoStock | null = null;
  for (let i = 0; i < compra.lineas.length; i++) {
    const l = compra.lineas[i]!;
    if (l.productoId !== productoId) continue;
    const desde: EstadoStock = estado ?? { ...l.antes };
    const cantidad = cantidadDe(i);
    estado = {
      cantidad: addQty(desde.cantidad, cantidad),
      costoPromedio: weightedAverageCost(desde.cantidad, desde.costoPromedio, cantidad, unitCostFromTotal(l.costoCordobas, cantidad)),
    };
  }
  return estado?.costoPromedio ?? ZERO_COST;
}

/**
 * The sales that took a purchase's cost, costed again at `costoDespues`: of its product at its
 * place, from its day on, written after it, each line at the cost the purchase left (`costoAntes`).
 * What each comes to, and what that moves on the books, net of what its returns took back. A
 * corrected quantity and a corrected cost both cost them again (`compra.cantidad_corregida`,
 * `compra.costo_corregido`). `ventas`, the product's sales there from the purchase's day
 * (`Reader.ventasDeProducto`).
 */
function recostearVentas(
  compra: Compra,
  productoId: string,
  costoAntes: UnitCost,
  costoDespues: UnitCost,
  ventas: readonly Venta[],
): { ventas: VentaRecosteada[]; aVendido: number } {
  const lugar = compra.lugar ?? -Infinity;
  const recosteadas: VentaRecosteada[] = [];
  let aVendido = 0;
  for (const v of costoAntes === costoDespues ? [] : ventas) {
    if (v.anulada || v.ubicacion !== compra.ubicacion || v.fecha < compra.fecha) continue;
    // Last written before the purchase: made before it, at a cost that only happens to be the same.
    if (v.rev < lugar) continue;
    let tocada = false;
    let costoTotal: number = v.costoTotal;
    let cambio = 0;
    const lineas = v.lineas.map((l) => {
      if (l.productoId !== productoId || l.costoUnitarioPosteado !== costoAntes) return l;
      tocada = true;
      const base = saleToBase(l.cantidad, l.factor);
      const devuelta = saleToBase(l.devuelta ?? ZERO_QTY, l.factor);
      const costoPosteado = costOf(costoDespues, base);
      // On the books, what it posted less what its returns took back (`valorDevuelto`).
      cambio += costoPosteado - costOf(costoDespues, devuelta) - (costOf(costoAntes, base) - costOf(costoAntes, devuelta));
      costoTotal += costoPosteado - l.costoPosteado;
      return { ...l, costoPosteado, costoUnitarioPosteado: costoDespues };
    });
    if (!tocada) continue;
    aVendido += cambio;
    recosteadas.push({ venta: v, lineas, costoTotal: centavos(costoTotal), diferencia: centavos(cambio) });
  }
  return { ventas: recosteadas, aVendido };
}

/** A sale costed again by a purchase's corrected quantity: its lines and total as they become, and what that moves on the books. */
export interface VentaRecosteada {
  venta: Venta;
  lineas: VentaLinea[];
  costoTotal: Centavos;
  /** What its cost of sales changes by, net of what its returns took back. */
  diferencia: Centavos;
}

/** What a purchase line's corrected quantity does (`corregirCantidadDeCompra`). Amounts are debits when positive. */
export interface CorreccionDeCantidad {
  productoId: string;
  /** What came, less what the line said. */
  diferencia: Milli;
  /** What each unit cost right after the purchase: as it was entered, and as corrected. */
  costoAntes: UnitCost;
  costoDespues: UnitCost;
  /** A count since the purchase found how many there really are: the stock keeps its quantity. */
  contado: boolean;
  /** The stock still at the purchase's cost, so it takes the corrected one exactly. */
  soloEsta: boolean;
  estante: {
    antes: { cantidad: Milli; costoPromedio: UnitCost };
    despues: { cantidad: Milli; costoPromedio: UnitCost };
  };
  ventas: VentaRecosteada[];
  /** What the stock's worth changes by, exactly as its row's value does. */
  aEstante: Centavos;
  /** Cost of sales: the sales costed again, and without a count whatever else went at the purchase's cost. */
  aVendido: Centavos;
  /** After a count, what it valued at the wrong cost: against what it found over, or short. */
  aConteo: Centavos;
  cuentaConteo: 'SOBRANTE_INVENTARIO' | 'FALTANTE_INVENTARIO';
}

/**
 * What correcting the quantity on a purchase line does (`compra.cantidad_corregida`), worked out
 * before anything changes: the reducer books it and the screen says it, from the same arithmetic.
 *
 * What the line cost stays what it cost; each unit of it cost something else. Every sale that took
 * the purchase's cost — at its place, dated from the purchase on, at exactly the cost it left — is
 * costed again at the corrected one, so its gross profit is what it really made (AJ-000077,
 * 2026-10-13). A stock still at the purchase's cost takes the corrected one, exactly as if the
 * line had been typed right; one a later receipt has mixed since takes back what its sales gave
 * back, which is as near as anything can say without the history.
 *
 * A count after the purchase already found how many there really are, valued at the cost the
 * wrong quantity gave: the quantity stays what it found, and what it booked over or short at that
 * cost is put right. Without one, the units the line left out come onto the shelf (or the ones it
 * had too many go off it).
 *
 * A later purchase of the product that left the same cost — the same sack typed as one unit again
 * the next week — makes its sales and this one's the same to look at: the later one is corrected
 * first (`posterior`), and then this one's are told apart by their cost. `compras` is every
 * purchase of the product at the place (`Reader.comprasDeProducto`).
 */
export function corregirCantidadDeCompra(
  compra: Compra,
  indice: number,
  cantidadBase: Milli,
  existencia: Pick<Existencia, 'cantidad' | 'costoPromedio' | 'contadoEn'> | undefined,
  ventas: readonly Venta[],
  compras: readonly Compra[],
): CorreccionDeCantidad | { rechazo: string; posterior?: Pick<Compra, 'id' | 'fecha'> } {
  if (compra.anulada) return { rechazo: 'Esa compra está anulada' };
  const linea = compra.lineas[indice];
  if (!linea) return { rechazo: 'Esa línea no existe' };
  // What went back went at what each unit cost then: a return settles it with the supplier.
  if ((linea.devuelta ?? 0) > 0) return { rechazo: 'Esa línea ya tiene devoluciones al proveedor' };
  if (cantidadBase <= 0) return { rechazo: 'La cantidad tiene que ser más que cero' };
  if (cantidadBase === linea.cantidadBase) return { rechazo: 'Esa ya es la cantidad de la línea' };

  const diferencia = subQty(cantidadBase, linea.cantidadBase);
  const comoEsta = (c: Compra) => (i: number) => c.lineas[i]!.cantidadBase;
  const costoAntes = costoQueDejo(compra, linea.productoId, comoEsta(compra));
  const costoDespues = costoQueDejo(compra, linea.productoId, (i) => (i === indice ? cantidadBase : compra.lineas[i]!.cantidadBase));
  const lugar = compra.lugar ?? -Infinity;
  const posterior = compras.find(
    (c) => c.id !== compra.id && !c.anulada && (c.lugar ?? -Infinity) > lugar && costoQueDejo(c, linea.productoId, comoEsta(c)) === costoAntes,
  );
  if (posterior) {
    return {
      rechazo: `Después de esta compra entró otra de este producto al mismo costo, la del ${posterior.fecha}: corrija primero esa`,
      posterior: { id: posterior.id, fecha: posterior.fecha },
    };
  }

  const { ventas: recosteadas, aVendido } = recostearVentas(compra, linea.productoId, costoAntes, costoDespues, ventas);

  const contadoEn = existencia?.contadoEn;
  const contado =
    contadoEn != null &&
    // A purchase from before its moment was kept counts as counted by any count from its day on.
    (compra.momento != null ? compra.momento < Date.parse(contadoEn) : fechaEnNicaragua(contadoEn) >= compra.fecha);
  const cantidad = existencia?.cantidad ?? ZERO_QTY;
  const costoPromedio = existencia?.costoPromedio ?? ZERO_COST;
  const soloEsta = existencia !== undefined && costoPromedio === costoAntes;
  const cantidadDespues = contado ? cantidad : addQty(cantidad, diferencia);
  let costoNuevo = costoPromedio;
  if (soloEsta) costoNuevo = costoDespues;
  else if (!contado) {
    // Mixed since: what the sales gave back, into what is left.
    const valor = costOf(costoPromedio, cantidad) - aVendido;
    if (cantidadDespues > 0 && valor >= 0) costoNuevo = unitCostFromTotal(centavos(valor), cantidadDespues);
  }
  const aEstante = costOf(costoNuevo, cantidadDespues) - costOf(costoPromedio, cantidad);
  // The rest: what a count valued at the wrong cost, or what other exits took at it.
  const resto = -(aEstante + aVendido);

  return {
    productoId: linea.productoId,
    diferencia,
    costoAntes,
    costoDespues,
    contado,
    soloEsta,
    estante: { antes: { cantidad, costoPromedio }, despues: { cantidad: cantidadDespues, costoPromedio: costoNuevo } },
    ventas: recosteadas,
    aEstante: centavos(aEstante),
    aVendido: centavos(contado ? aVendido : aVendido + resto),
    aConteo: centavos(contado ? resto : 0),
    cuentaConteo: diferencia > 0 ? 'SOBRANTE_INVENTARIO' : 'FALTANTE_INVENTARIO',
  };
}

async function corregirCantidad(
  event: Extract<AnyDomainEvent, { type: 'compra.cantidad_corregida' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });
  const compra = await read.compra(p.compraId);
  if (!compra) return rechazo('Esa compra no existe');
  const linea = compra.lineas[p.indice];
  const actual = linea ? await read.existencia(linea.productoId, compra.ubicacion) : undefined;
  const ventas = linea ? await read.ventasDeProducto(linea.productoId, compra.ubicacion, compra.fecha) : [];
  const compras = linea ? await read.comprasDeProducto(linea.productoId, compra.ubicacion) : [];
  const c = corregirCantidadDeCompra(compra, p.indice, p.cantidadBase as Milli, actual, ventas, compras);
  if ('rechazo' in c) return rechazo(c.rechazo);
  const { productoId } = c;

  const id = existenciaId(productoId, compra.ubicacion);
  const effects: Effect[] = [
    {
      kind: 'existencia.set',
      id,
      row: {
        ...(actual ?? { id, productoId, ubicacion: compra.ubicacion }),
        cantidad: c.estante.despues.cantidad,
        costoPromedio: c.estante.despues.costoPromedio,
        /*
         * Its last movement: voided after this, the purchase would put the stock back as it stood
         * before it, and take out the purchase's total from what the correction has rounded to its
         * own worth. Goods go back to the supplier with a return now.
         */
        ultimoMovimientoId: event.id,
        rev,
      },
    },
    {
      kind: 'compra.patch',
      id: compra.id,
      rev,
      patch: { lineas: compra.lineas.map((l, i) => (i === p.indice ? { ...l, cantidadBase: p.cantidadBase as Milli } : l)) },
    },
    ...c.ventas.map(
      (v): Effect => ({ kind: 'venta.patch', id: v.venta.id, rev, patch: { lineas: v.lineas, costoTotal: v.costoTotal } }),
    ),
  ];
  // Its batch received what really came; what is left of it, unless a count already said.
  const lote = linea!.lote
    ? (await read.lotesDe(productoId, compra.ubicacion)).find((x) => x.id === loteId(productoId, linea!.lote!.numero, compra.ubicacion))
    : undefined;
  if (lote) {
    effects.push({
      kind: 'lote.upsert',
      id: lote.id,
      row: {
        ...lote,
        cantidadRecibida: addQty(lote.cantidadRecibida, c.diferencia),
        cantidadDisponible: c.contado ? lote.cantidadDisponible : addQty(lote.cantidadDisponible, c.diferencia),
        rev,
      },
    });
  }

  const lado = (cuenta: AccountKey, monto: Centavos, texto: string): JournalLine =>
    monto > 0 ? debit(cuenta, monto, texto) : credit(cuenta, centavos(-monto), texto);
  effects.push({
    kind: 'asiento',
    entry: entry(event.id, fechaEnNicaragua(event.clientTs), `Cantidad de compra corregida: ${p.motivo}`, [
      lado(cuentaInventario(compra.ubicacion), c.aEstante, 'Lo que queda'),
      lado('COSTO_VENTAS', c.aVendido, 'Lo ya vendido'),
      lado(c.cuentaConteo, c.aConteo, 'Ya contado'),
    ]),
  });
  return { effects, rejections: [] };
}

/** How a purchase was really paid, as a correction says it (`compra.forma_pago_corregida`). */
export interface PagoCorregido {
  formaPago: FormaPago;
  /** The till it came out of, in córdobas in cash. */
  sesionId?: string | null;
  /** The córdoba account it went from, by transfer. */
  cuenta?: string | null;
  /** When it is due, on credit. */
  vencePago?: string | null;
}

/** Why a purchase's payment cannot be corrected so, for the screen to say in its own language. */
export type PorQueElPagoNo =
  | 'anulada'
  | 'deudaAnterior'
  | 'enDolares'
  | 'enPartes'
  | 'conDevoluciones'
  | 'conAbonos'
  | 'igual'
  | 'sinVencimiento'
  | 'cuentaCerrada'
  | 'otraCaja'
  | 'cajaCerrada'
  | 'noAlcanza';

/** One side of a payment corrected: where the books put it (`adondeVaLaCorreccion`), and on which account. */
export interface LadoDelPago {
  destino: 'deuda' | 'gaveta' | 'corte' | 'cuenta' | 'conteo';
  /** The account the payment names, as the screen says it. */
  cuenta: AccountKey;
  /** The account its line goes on: that one, or what a count since booked the difference to. */
  linea: AccountKey;
}

/** What correcting how a purchase was paid does (`corregirFormaDePago`). */
export interface CorreccionDePago {
  monto: Centavos;
  /** The money back where the purchase said it came from: a debit. */
  vuelve: LadoDelPago;
  /** The money out of where it really came from: a credit. */
  sale: LadoDelPago;
  /**
   * Both sides the same money — cash in córdobas or in dollars, both general cash, counted since or
   * not; two accounts each squared since — so only what the purchase says changes, and nothing is booked.
   */
  mismaPlata: boolean;
}

/**
 * What correcting how a purchase was paid does (`compra.forma_pago_corregida`), worked out before
 * anything changes: the reducer books it and the sheet says it, from the same reading.
 *
 * What the goods cost stays. The money goes back where the purchase said it came from and comes
 * out of where it did, each side where a cost correction's difference would go
 * (`adondeVaLaCorreccion`): what is owed; a drawer not counted since the purchase; one counted
 * since, whose corte already found the money over or short; the account itself; or what a count of
 * it after the purchase booked the difference to. A transfer written down as cash left general
 * cash over at its count and the bank short at its statement: corrected, both differences are
 * explained, and neither account moves again.
 *
 * Only a purchase paid whole, in córdobas: each part of one paid in parts is its own payment, one
 * in dollars came in at the rate its way of paying took, and returns and payments made on credit
 * moved money after it.
 */
export function corregirFormaDePago(
  compra: Compra,
  nuevo: PagoCorregido,
  sesiones: { antes?: SesionCaja | undefined; despues?: SesionCaja | undefined },
  config: Settings,
): CorreccionDePago | { rechazo: string; por: PorQueElPagoNo } {
  const no = (por: PorQueElPagoNo, rechazo: string) => ({ rechazo, por });
  if (compra.anulada) return no('anulada', 'Esa compra está anulada');
  if (esDeudaAnterior(compra)) return no('deudaAnterior', 'Es una deuda anterior: se corrige, o se pone en cero, donde se anotó');
  if (compra.moneda === 'USD') return no('enDolares', 'Una compra en dólares entró a la tasa que tomó su forma de pago: esa no se corrige aquí');
  if ((compra.pagos ?? []).some((x) => x.conLaCompra)) return no('enPartes', 'Esa compra se pagó en partes: cada parte es su propio pago');
  if ((compra.devuelto ?? 0) > 0 || (compra.devueltoACuenta ?? 0) > 0 || compra.lineas.some((l) => (l.devuelta ?? 0) > 0)) {
    return no('conDevoluciones', 'Esa compra ya tiene devoluciones al proveedor');
  }
  if (compra.formaPago === 'credito' && compra.pagado > 0) return no('conAbonos', 'Esa compra ya tiene abonos al proveedor: anúlelos primero');
  if (nuevo.formaPago === 'credito' && !nuevo.vencePago) return no('sinVencimiento', 'Una compra al crédito necesita fecha de vencimiento');
  const banco = (p: { formaPago: FormaPago; cuenta?: string | null | undefined }) =>
    p.formaPago === 'transferencia' ? cuentaDeTransferencia(p.cuenta as CuentaBanco | null | undefined) : null;
  if (nuevo.formaPago === compra.formaPago && (nuevo.sesionId ?? null) === (compra.sesionId ?? null) && banco(nuevo) === banco(compra)) {
    return no('igual', 'Esa ya es la forma de pago de la compra');
  }
  if (nuevo.formaPago === 'transferencia') {
    const cerrada = cuentaCerradaParaSiempre(config, nuevo.cuenta as CuentaBanco | null | undefined);
    if (cerrada) return no('cuentaCerrada', cerrada);
  }
  const despues = nuevo.sesionId ? sesiones.despues : undefined;
  if (nuevo.sesionId) {
    if (!despues || despues.id !== nuevo.sesionId || despues.ubicacion !== compra.ubicacion) {
      return no('otraCaja', 'Esa caja no es de donde llegó la compra');
    }
    // Open the day it was entered: by the day, not the minute, so a phone's clock a little off is no matter.
    const dia = compra.momento != null ? fechaEnNicaragua(new Date(compra.momento)) : compra.fecha;
    const abiertaEseDia =
      fechaEnNicaragua(despues.abiertaEn) <= dia && (!despues.cerradaEn || fechaEnNicaragua(despues.cerradaEn) >= dia);
    if (!abiertaEseDia) return no('cajaCerrada', 'Esa caja no estaba abierta el día que entró esa compra');
  }

  const lado = (pago: Pick<Compra, 'formaPago' | 'cuenta' | 'lugar' | 'fecha'>, sesion: SesionCaja | undefined, vuelve: boolean): LadoDelPago => {
    const { destino, cuenta } = adondeVaLaCorreccion(pago, sesion, config);
    const linea: AccountKey =
      destino === 'corte'
        ? vuelve
          ? 'SOBRANTE_CAJA'
          : 'FALTANTE_CAJA'
        : destino === 'conteo'
          ? cuenta === 'CAJA_FUERTE'
            ? vuelve
              ? 'SOBRANTE_GENERAL'
              : 'FALTANTE_GENERAL'
            : 'DIFERENCIAS_CONCILIACION'
          : cuenta;
    return { destino, cuenta, linea };
  };
  const monto = compra.totalCordobas;
  const vuelve = lado(compra, compra.sesionId ? sesiones.antes : undefined, true);
  const sale = lado({ formaPago: nuevo.formaPago, cuenta: nuevo.cuenta ?? null, lugar: compra.lugar ?? null, fecha: compra.fecha }, despues, false);
  if (sale.destino === 'gaveta' && despues && efectivoEsperado(despues) < monto) {
    return no('noAlcanza', `En la gaveta no hay tanto: se esperan ${formatMoney(efectivoEsperado(despues))}`);
  }
  const mismaPlata = vuelve.linea === sale.linea || (vuelve.destino === 'conteo' && sale.destino === 'conteo' && vuelve.cuenta === sale.cuenta);
  return { monto, vuelve, sale, mismaPlata };
}

/**
 * Where a payment's correction puts the money back and takes it out of, once counted since: what
 * those counts found moves instead (`reencontrarElPago`). Both the same count (the same safe), what
 * the first left is the second's. The reducer books it and the sheet says it, from the same reading.
 */
export async function reencontrarLosPagos(
  read: Reader,
  config: Settings,
  c: CorreccionDePago,
  compra: Pick<Compra, 'sesionId' | 'lugar'>,
  sesionId: string | null,
  hasta: number,
): Promise<{ vuelveA: Reencontrado | null; saleDe: Reencontrado | null } | { rechazo: string }> {
  if (c.mismaPlata) return { vuelveA: null, saleDe: null };
  const vuelveA = await reencontrarElPago(read, config, c.vuelve, { sesionId: compra.sesionId, lugar: compra.lugar }, c.monto, hasta);
  if (vuelveA && 'rechazo' in vuelveA) return vuelveA;
  const configTras = vuelveA ? { ...config, conteosCorregidos: { ...config.conteosCorregidos, ...corregidos(vuelveA) } } : config;
  const saleDe = await reencontrarElPago(read, configTras, c.sale, { sesionId, lugar: compra.lugar }, -c.monto, hasta);
  if (saleDe && 'rechazo' in saleDe) return saleDe;
  return { vuelveA, saleDe };
}

async function corregirPago(
  event: Extract<AnyDomainEvent, { type: 'compra.forma_pago_corregida' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const compra = await read.compra(p.compraId);
  if (!compra) return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa compra no existe' }] };
  const antes = compra.sesionId ? await read.sesionCaja(compra.sesionId) : undefined;
  const despues = p.sesionId ? await read.sesionCaja(p.sesionId) : undefined;
  const config = await read.config();
  const c = corregirFormaDePago(
    compra,
    { formaPago: p.formaPago, sesionId: p.sesionId ?? null, cuenta: p.cuenta ?? null, vencePago: p.vencePago ?? null },
    { antes, despues },
    config,
  );
  if ('rechazo' in c) return { effects: [], rejections: [{ eventId: event.id, reason: c.rechazo }] };

  const encontrados = await reencontrarLosPagos(read, config, c, compra, p.sesionId ?? null, lugarEnElLog(event));
  if ('rechazo' in encontrados) return { effects: [], rejections: [{ eventId: event.id, reason: encontrados.rechazo }] };
  const { vuelveA, saleDe } = encontrados;

  const effects: Effect[] = [
    {
      kind: 'compra.patch',
      id: compra.id,
      rev,
      patch: {
        formaPago: p.formaPago,
        sesionId: p.sesionId ?? null,
        cuenta: p.formaPago === 'transferencia' ? (p.cuenta ?? null) : null,
        vencePago: p.formaPago === 'credito' ? p.vencePago! : null,
        // On credit nothing is paid yet: one with payments made on it is refused. Any other way, all of it.
        pagado: p.formaPago === 'credito' ? ZERO : compra.totalCordobas,
      },
    },
  ];
  // A drawer not counted since gets back what it was said to pay, and the one it came out of expects it gone.
  if (c.vuelve.destino === 'gaveta' && antes) {
    effects.push({ kind: 'sesion.patch', id: antes.id, rev, patch: { pagado: subMoney(antes.pagado ?? ZERO, c.monto) } });
  }
  if (c.sale.destino === 'gaveta' && despues) {
    effects.push({ kind: 'sesion.patch', id: despues.id, rev, patch: { pagado: addMoney(despues.pagado ?? ZERO, c.monto) } });
  }

  if (!c.mismaPlata) {
    const linea = (lado: LadoDelPago, debe: boolean, forma: FormaPago, cuenta: string | null | undefined, memo: string): JournalLine =>
      lado.destino === 'cuenta'
        ? lineaDelPago(debe ? 'debe' : 'haber', forma, cuenta, c.monto, 0, memo)
        : debe
          ? debit(lado.linea, c.monto, memo)
          : credit(lado.linea, c.monto, memo);
    effects.push({
      kind: 'asiento',
      entry: entry(event.id, fechaEnNicaragua(event.clientTs), `Forma de pago de compra corregida: ${p.motivo}`, [
        ...(vuelveA ? vuelveA.lineas : [linea(c.vuelve, true, compra.formaPago, compra.cuenta, 'Como decía la compra')]),
        ...(saleDe ? saleDe.lineas : [linea(c.sale, false, p.formaPago, p.cuenta, 'Como se pagó')]),
      ]),
    });
    const cambian = { ...corregidos(vuelveA), ...corregidos(saleDe) };
    if (Object.keys(cambian).length > 0) {
      effects.push({ kind: 'config.set', clave: 'conteosCorregidos', valor: { ...config.conteosCorregidos, ...cambian }, rev });
    }
  }
  const cajas = [...(c.vuelve.destino === 'gaveta' && antes ? [antes] : []), ...(c.sale.destino === 'gaveta' && despues ? [despues] : [])];
  const razon = noCuadraLaCorreccion(effects, {
    sesiones: cajas.filter((x, i) => cajas.findIndex((y) => y.id === x.id) === i),
    relevadas: new Set(),
    conteos: conteosQueCambian(vuelveA, saleDe),
    corregidos: config.conteosCorregidos,
  });
  if (razon) return { effects: [], rejections: [{ eventId: event.id, reason: `La corrección no cuadra (${razon}): no se asentó` }] };
  return { effects, rejections: [] };
}

async function anular(
  event: Extract<AnyDomainEvent, { type: 'compra.anulada' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const { compraId, motivo } = event.payload;

  const compra = await read.compra(compraId);
  if (!compra) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa compra no existe' }] };
  }
  if (compra.anulada) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa compra ya está anulada' }] };
  }
  // It brought no goods to put back, and it went against Capital, not inventory.
  if (esDeudaAnterior(compra)) {
    return {
      effects: [],
      rejections: [{ eventId: event.id, reason: 'Es una deuda anterior: se corrige, o se pone en cero, donde se anotó' }],
    };
  }
  // Its own parts go back with it (`conLaCompra`); a payment made after it is another fact.
  const partes = (compra.pagos ?? []).filter((x) => x.conLaCompra && !x.anulado);
  const enPartes = partes.reduce((a, x) => addMoney(a, x.monto), ZERO);
  if (compra.formaPago === 'credito' && compra.pagado > enPartes) {
    // Reversing the whole payable would erase payments that really happened.
    return {
      effects: [],
      rejections: [
        { eventId: event.id, reason: 'Esa compra ya tiene abonos. Registre una devolución en su lugar.' },
      ],
    };
  }
  // Goods sent back for nothing still left: the annulment would put the stock back as it was.
  if ((compra.devuelto ?? 0) > 0 || compra.lineas.some((l) => (l.devuelta ?? 0) > 0)) {
    return {
      effects: [],
      rejections: [{ eventId: event.id, reason: 'Esa compra ya tiene devoluciones al proveedor.' }],
    };
  }

  /**
   * Voided, the money goes back where it was paid from, and a count since already found it gone:
   * out of a till, once that till was handed over or closed; otherwise once general cash, the bank
   * or the wallet was counted (`contadas.ts`). Put back on the books then, it would be money nobody
   * has, a shortage at the next count that nobody caused (2026-10-13). The goods go back to the
   * supplier with a return instead, which moves money only if the supplier gives some back. Only a
   * void that says so (`sinContar`, every one from this version on): one from before stands.
   */
  if (event.payload.sinContar) {
    const lugar = compra.lugar ?? -Infinity;
    const devuelva = 'Registre una devolución al proveedor en su lugar';
    for (const sesionId of new Set([compra.sesionId, ...partes.map((x) => x.sesionId)].filter((x): x is string => Boolean(x)))) {
      const caja = await read.sesionCaja(sesionId);
      if (caja?.estado !== 'abierta') {
        return { effects: [], rejections: [{ eventId: event.id, reason: `Esa compra se pagó de una caja que ya se cerró: su corte ya contó ese dinero. ${devuelva}` }] };
      }
      if ((caja.entregadaRev ?? -Infinity) > lugar) {
        return { effects: [], rejections: [{ eventId: event.id, reason: `Después de esa compra la caja cambió de manos: esa entrega ya contó la gaveta. ${devuelva}` }] };
      }
    }
    const config = await read.config();
    const deCuentas = [
      ...(!compra.sesionId && compra.formaPago !== 'credito' ? [{ forma: compra.formaPago, cuenta: compra.cuenta }] : []),
      ...partes.filter((x) => !x.sesionId).map((x) => ({ forma: x.formaPago, cuenta: x.cuenta })),
    ];
    for (const { forma, cuenta: suya } of deCuentas) {
      const cuenta = forma === 'transferencia' ? cuentaDeTransferencia(suya as CuentaBanco | null | undefined) : CUENTA_PAGO[forma as Exclude<FormaPago, 'credito'>];
      if (cuenta && contadaDespues(config, cuenta, lugar, compra.fecha)) {
        return { effects: [], rejections: [{ eventId: event.id, reason: `${yaLaContaron(cuenta, 'esa compra', config)}. ${devuelva}` }] };
      }
    }
  }

  /**
   * All or nothing.
   *
   * If anything moved this stock after the purchase, the average has already absorbed it and
   * there is no arithmetic that undoes this purchase alone. Refusing is the honest answer; the
   * correct action then is an adjustment, which is a new fact rather than an erasure.
   */
  const restaurar = new Map<string, { cantidad: Milli; costoPromedio: UnitCost; productoId: string }>();
  /** Each line as the purchase left it: nothing has moved it since. */
  const actuales = new Map<string, Existencia>();
  for (const linea of compra.lineas) {
    const clave = existenciaId(linea.productoId, compra.ubicacion);
    if (restaurar.has(clave)) continue;

    const actual = await read.existencia(linea.productoId, compra.ubicacion);
    if (!actual || actual.ultimoMovimientoId !== compraId) {
      return {
        effects: [],
        rejections: [
          {
            eventId: event.id,
            reason:
              'Ya hay movimientos posteriores sobre estos productos. Registre un ajuste de inventario en vez de anular.',
          },
        ],
      };
    }
    restaurar.set(clave, {
      cantidad: linea.antes.cantidad,
      costoPromedio: linea.antes.costoPromedio,
      productoId: linea.productoId,
    });
    actuales.set(linea.productoId, actual);
  }

  /**
   * Paid out of a drawer, it goes back into it while that till is open: the rest when it came
   * from there, and the parts that did, in one patch of the till. On credit, the rest is what is
   * still owed; paid, what `formaPago` paid, as a cost correction left it, never below nothing
   * (`corregirCosto`).
   */
  const resto = subMoney(compra.totalCordobas, enPartes);
  const gaveta = compra.sesionId ?? partes.find((x) => x.sesionId)?.sesionId;
  const aLaGaveta = partes.reduce((a, x) => (x.sesionId ? addMoney(a, x.monto) : a), compra.sesionId ? resto : ZERO);
  const vuelta = gaveta ? await devolucionDePago(read, gaveta, aLaGaveta, rev) : null;

  const effects: Effect[] = [...(vuelta?.efectos ?? [])];
  // Its batches go with it: what it put in them comes back out, and nothing else does.
  const lotes = new Map<string, Lote>();
  for (const linea of compra.lineas) {
    if (!linea.lote) continue;
    const id = loteId(linea.productoId, linea.lote.numero, compra.ubicacion);
    const lote = lotes.get(id) ?? (await read.lotesDe(linea.productoId, compra.ubicacion)).find((x) => x.id === id);
    if (!lote) continue;
    lotes.set(id, {
      ...lote,
      cantidadRecibida: subQty(lote.cantidadRecibida, linea.cantidadBase),
      cantidadDisponible: subQty(lote.cantidadDisponible, linea.cantidadBase),
      rev,
    });
  }
  for (const [id, row] of lotes) effects.push({ kind: 'lote.upsert', id, row });
  for (const [clave, estado] of restaurar) {
    const [productoId, ubicacion] = partirClave(clave);
    const fila: Existencia = {
      id: clave,
      productoId,
      ubicacion,
      cantidad: estado.cantidad,
      costoPromedio: estado.costoPromedio,
      ultimoMovimientoId: event.id,
      rev,
    };
    effects.push({ kind: 'existencia.set', id: clave, row: fila });
  }

  effects.push({
    kind: 'compra.patch',
    id: compraId,
    rev,
    patch: {
      anulada: true,
      motivoAnulacion: motivo,
      // Its parts went back with it: none is there to void again.
      ...(partes.length > 0
        ? {
            pagos: compra.pagos!.map((x) =>
              x.conLaCompra && !x.anulado ? { ...x, anulado: true, motivoAnulacion: motivo, anuladoConLaCompra: true as const } : x,
            ),
          }
        : {}),
    },
  });

  // Reversing entry, not a deletion. A corrected receipt is a new receipt.
  const vuelveA = (forma: FormaPago, deLaGaveta: boolean, monto: Centavos, cuenta?: string | null, dolares = 0) =>
    deLaGaveta && vuelta ? debit(vuelta.cuenta, monto) : lineaDelPago('debe', forma, cuenta, monto, dolares);
  effects.push({
    kind: 'asiento',
    entry: entry(event.id, compra.fecha, `Anulación de compra: ${motivo}`, [
      ...partes.map((x) => vuelveA(x.formaPago, Boolean(x.sesionId), x.monto, x.cuenta)),
      vuelveA(compra.formaPago, Boolean(compra.sesionId), resto, compra.cuenta, compra.dolares ?? 0),
      credit(cuentaInventario(compra.ubicacion), compra.totalCordobas),
    ]),
  });
  // What stock below zero cost beyond what was booked goes back out with the purchase that told it.
  const ajuste = ajusteVendidoAntes(
    event.id,
    compra.fecha,
    recibidosDeCompra(compra.lineas, compra.ubicacion, (productoId) => actuales.get(productoId)!),
    true,
  );
  if (ajuste) effects.push(ajuste);

  return { effects, rejections: [] };
}

async function saldoInicial(
  event: Extract<AnyDomainEvent, { type: 'inventario.saldo_inicial' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;

  const producto = await read.producto(p.productoId);
  if (!producto) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'El producto no existe' }] };
  }

  const actual = await read.existencia(p.productoId, p.ubicacion);
  const valorAntes = actual ? costOf(actual.costoPromedio, actual.cantidad) : ZERO;

  const cantidad = p.cantidadBase as Milli;
  const costo = p.costoUnitario as UnitCost;
  const valorDespues = costOf(costo, cantidad);
  const delta = subMoney(valorDespues, valorAntes);

  const clave = existenciaId(p.productoId, p.ubicacion);
  const effects: Effect[] = [
    {
      kind: 'existencia.set',
      id: clave,
      row: {
        id: clave,
        productoId: p.productoId,
        ubicacion: p.ubicacion,
        cantidad,
        costoPromedio: costo,
        ultimoMovimientoId: event.id,
        rev,
      },
    },
  ];

  /**
   * Opening stock posts against Capital, not an expense: the owner contributed these goods,
   * they did not spend money on them through the business. Booking it as a purchase would
   * invent a cost that was never paid and depress the first month's profit.
   */
  if (delta !== 0) {
    const monto = centavos(Math.abs(delta));
    const cuenta = cuentaInventario(p.ubicacion);
    effects.push({
      kind: 'asiento',
      entry: entry(
        event.id,
        // The day the count was made. This used to read the clock while reducing, so a replay
        // re-dated the entry to whenever the log was replayed and two devices disagreed.
        fechaEnNicaragua(event.clientTs),
        `Inventario inicial: ${producto.nombre}`,
        delta > 0
          ? [debit(cuenta, monto), credit('CAPITAL', monto)]
          : [debit('CAPITAL', monto), credit(cuenta, monto)],
      ),
    });
  }

  return { effects, rejections: [] };
}

/**
 * An invoice the shop already owed a supplier before it kept its books here.
 *
 * A purchase on credit with no goods, so that paying it off, the list of what is owed and the
 * balance sheet all treat it as the purchase it once was. Against Capital, as opening stock is:
 * the goods it paid for are in that stock already, and booking them again as a purchase would
 * count them twice. Only the difference from what was set before is posted, so it corrects
 * itself when sent again; what has already been paid on it stays paid.
 */
async function deudaAnterior(
  event: Extract<AnyDomainEvent, { type: 'compra.saldo_inicial' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });

  const proveedor = await read.proveedor(p.proveedorId);
  if (!proveedor) return rechazo('El proveedor no existe');
  const compra = await read.compra(p.compraId);
  if (compra && !esDeudaAnterior(compra)) return rechazo('Esa compra trajo mercadería: no es una deuda anterior');
  if (compra?.anulada) return rechazo('Esa compra está anulada');
  const monto = centavos(p.monto);
  if (!compra && monto === 0) return rechazo('Una deuda anterior necesita cuánto se debe');
  const cubierto = compra ? addMoney(compra.pagado, compra.devueltoACuenta ?? ZERO) : ZERO;
  if (monto < cubierto) return rechazo(`A esa deuda ya se le abonó ${formatMoney(cubierto)}: no puede quedar en menos`);

  const datos = {
    proveedorId: p.proveedorId,
    fecha: p.fecha,
    documento: p.documento ?? null,
    vencePago: p.vencePago ?? null,
    totalCordobas: monto,
  };
  const effects: Effect[] = [
    compra
      ? { kind: 'compra.patch', id: compra.id, rev, patch: datos }
      : {
          kind: 'compra.upsert',
          id: p.compraId,
          row: {
            id: p.compraId,
            ...datos,
            moneda: 'NIO',
            tasaCambio: null,
            flete: ZERO,
            ubicacion: 'tienda',
            formaPago: 'credito',
            lineas: [],
            pagado: ZERO,
            anulada: false,
            motivoAnulacion: null,
            lugar: lugarEnElLog(event),
            rev,
          },
        },
  ];

  const diferencia = subMoney(monto, compra?.totalCordobas ?? ZERO);
  if (diferencia !== 0) {
    const cuanto = centavos(Math.abs(diferencia));
    effects.push({
      kind: 'asiento',
      entry: entry(
        event.id,
        // The day it is written down, as with opening stock; the invoice's own date is on the row.
        fechaEnNicaragua(event.clientTs),
        `Deuda anterior con ${proveedor.nombre}${p.documento ? ` (${p.documento})` : ''}`,
        diferencia > 0
          ? [debit('CAPITAL', cuanto), credit('CUENTAS_POR_PAGAR', cuanto)]
          : [debit('CUENTAS_POR_PAGAR', cuanto), credit('CAPITAL', cuanto)],
      ),
    });
  }
  return { effects, rejections: [] };
}

function partirClave(clave: string): [string, string] {
  const corte = clave.indexOf(':');
  return [clave.slice(0, corte), clave.slice(corte + 1)];
}
